import { InvestorBidStatus, PricingType } from '~/constants';
import { sizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import {
  calculateStorageFeePerWdc,
  getBuyerFeeData,
  getFees,
  getStorageFees,
} from '~/utils/calculators/fees';
import {
  ProjectBid,
  BidCalculation,
  ProjectCosts,
  ItcCalculation,
  OfftakeCalculation,
  CostsData
} from '~/types/project';
import {
  getExpectedItc,
  getExpectedItcCost,
  normalizeToOverall,
  normalizeToPerWdc,
  normalizeToOverallStorage
} from './costsFns';

import { System } from '.';

const calculateProjectCostsForBids = (
  project: any,
  permissions: any
) => {
  const results: ProjectCosts = [];
  project.bidSummary?.forEach((investor: any) => {
    const bidObj = {} as any;
    bidObj.bid = investor.bid || null;
    const projectCosts = calculateProjectCosts(
      project,
      permissions,
      bidObj
    );
    results.push(projectCosts);
  });
  return results;
};

export const calculateProjectCosts = (
  project: any,
  permissions: any,
  bid?: any
) => {

  // if (project.parentId) {
  //   project = fetchProject(project.parentId);
  // }

  const isInvestor = permissions.isAdmin || permissions.hasProjectInvestorAccess;
  const isDeveloper = permissions.isAdmin || permissions.hasProjectDeveloperAccess;
  const isEpcProject = project?.pricingType === PricingType.ACQUISITION || project?.pricingType === PricingType.DEV_FEE;
  const isItcProject = project?.pricingType === PricingType.ITC;
  const isOfftakeProject = project?.pricingType === PricingType.OFFTAKE;
  const hasStorage = project?.storage?.length > 0;
  const isStorageOnly = hasStorage && sizeKwdc(project) === 0;
  const totalStorageKwh = getProjectStorageSizeKwh(project);

  const result: ProjectCosts = {
    bid: {} as ProjectBid,
    epcCost: 0,
    epcCostUnit: '',
    sizeKwdc: 0,
    yearOneProduction: 0,
    epcCostTotal: 0,
    expectedItc: 0,
    expectedItcCost: 0,
    data: {} as CostsData,
    total: { total: 0 } as BidCalculation,
    perWdc: { total: 0 } as BidCalculation,
    perKwh: { total: 0 } as BidCalculation,
    itc: { total: 0 } as ItcCalculation,
    offtake: { total: 0 } as OfftakeCalculation
  };

  result.bid = (bid && bid.bid) ? bid.bid as any : null;

  if (result.bid && isEpcProject) {
    result.epcCost = result.bid.amount;
    result.epcCostUnit = result.bid.unit;
  } else {
    result.epcCost = project?.epcCost;
    result.epcCostUnit = project?.epcCostUnit;
  }

  result.sizeKwdc = sizeKwdc(project);

  result.epcCostTotal = normalizeToOverall(result.sizeKwdc, { amount: result.epcCost, unit: result.epcCostUnit } as ProjectBid);
  result.total.bid = result.bid ? (result.bid.unit === 'WDC' ? (result.bid.amount * 1000 * result.sizeKwdc) : result.bid.amount) : 0;
  result.perWdc.bid = result.bid ? (result.bid.unit === 'WDC') ? result.bid.amount : (result.bid.amount / (1000 * result.sizeKwdc)) : 0;

  result.expectedItc = getExpectedItc(project);
  if (isItcProject || isInvestor) {
    result.expectedItcCost = getExpectedItcCost(project);
  } else {
    result.expectedItcCost = getExpectedItcCost(project, result.epcCostTotal);
  }

  // Costs data
  result.data.isPendingBid = Boolean(!result.bid || result?.bid?.bidStatus === InvestorBidStatus.PENDING);
  result.data.isRejectedBid = Boolean(result.bid && (result.bid.bidStatus === InvestorBidStatus.REJECTED || result.bid.bidStatus === InvestorBidStatus.DECLINED));
  result.data.isEpcProject = isEpcProject;
  result.data.isItcProject = isItcProject;
  result.data.isOfftakeProject = isOfftakeProject;
  result.data.hasStorage = hasStorage;
  result.data.buyerFees = isInvestor ? getBuyerFeeData(project) || undefined : undefined;
  result.data.sellerFees = (isDeveloper && result.bid) ? result.bid.sellerFees : undefined;
  result.data.hasPercentFees = isInvestor
    ? Boolean(
      (
        result.data.buyerFees?.nrsFeeUnit === 'PERCENT_OF_BUDGET' ||
        result.data.buyerFees?.nrsFeeUnit === 'PERCENT_OF_ITC'
      ) && (result.data.buyerFees?.nrsFee && result.data.buyerFees?.nrsFee > 0))
    : Boolean(result.data.sellerFees?.sellerFeeUnit === 'PERCENT_OF_BUDGET' || result.data.sellerFees?.sellerFeeUnit === 'PERCENT_OF_ITC');

  //TODO Project typing
  const {
    INTERCONNECTION: interconnection = 0,
    OTHER_ITC: otherItc = 0,
    OTHER_NON_ITC: otherNonItc = 0
  } = project?.budgetCosts || {};

  // Overall costs
  result.total.interconnection = normalizeToOverall(result.sizeKwdc, interconnection);
  result.total.otherItc = normalizeToOverall(result.sizeKwdc, otherItc);
  result.total.otherNonItc = normalizeToOverall(result.sizeKwdc, otherNonItc);
  result.total.epcCost = normalizeToOverall(result.sizeKwdc, { amount: result.epcCost, unit: result.epcCostUnit } as ProjectBid);
  result.total.total = result.total.interconnection + result.total.otherItc + result.total.otherNonItc + result.total.epcCost;

  if (isEpcProject) {
    result.total.otherBudgetItemsTotal = result.total.interconnection + result.total.otherItc + result.total.otherNonItc;
    result.total.investorBidAmount = result.total.epcCost;
  }

  if (!isItcProject) {
    if (isInvestor) {
      result.total.nrsFee = getFees(project, result.total.epcCost, { bid, hasNrsFee: true }) ?? 0;
      result.total.buyerStorageFee = 0;

      if (hasStorage) {
        result.total.buyerStorageFee = getStorageFees(project, { hasBuyerStorageFee: true }) ?? 0;
        result.total.showBuyerStorageFees = result.total.buyerStorageFee !== 0;
      }

      result.total.feesTotal = result.total.nrsFee + result.total.buyerStorageFee;
      result.total.total += result.total.feesTotal;
      //result.total.showBuyerFees = result.total.nrsFee !== 0;
      result.total.showBuyerFees = true;
    }

    if (isDeveloper) {
      if (project.investorQueue && project.investorQueue.length > 0) {
        if (project.investorQueue[0].sellerFee) {
          if (project.investorQueue[0].sellerFeeUnit === 'DOLLARS_PER_WDC') {
            result.total.sellerFee = project.investorQueue[0].sellerFee * 1000 * result.sizeKwdc;
          } else {
            result.total.sellerFee = project.investorQueue[0].sellerFee;
          }
        }
        if (project.investorQueue[0].sellerStorageFee) {
          if (project.investorQueue[0].sellerStorageFeeUnit === 'DOLLARS_PER_KWH') {
            result.total.sellerStorageFee = project.investorQueue[0].sellerStorageFee * getProjectStorageSizeKwh(project);
          } else {
            result.total.sellerStorageFee = project.investorQueue[0].sellerStorageFee;
          }
        }
      }

      result.total.feesTotal = (result.total.sellerFee ?? 0) + (result.total.sellerStorageFee ?? 0);

      result.total.total -= result.total.feesTotal;

      result.total.showSellerFees = result.total.sellerFee !== 0;
    }
  }

  // Per Wdc costs
  result.perWdc.interconnection = normalizeToPerWdc(result.sizeKwdc, interconnection);
  result.perWdc.otherItc = normalizeToPerWdc(result.sizeKwdc, otherItc);
  result.perWdc.otherNonItc = normalizeToPerWdc(result.sizeKwdc, otherNonItc);
  result.perWdc.epcCost = normalizeToPerWdc(result.sizeKwdc, { amount: result.epcCost, unit: result.epcCostUnit } as ProjectBid);
  result.perWdc.total = result.perWdc.interconnection + result.perWdc.otherItc + result.perWdc.otherNonItc + result.perWdc.epcCost;

  if (isEpcProject) {
    result.perWdc.investorBidAmount = result.perWdc.epcCost;
  }

  if (isInvestor) {
    if (result.total.nrsFee) {
      result.perWdc.nrsFee = (result.total.nrsFee / (result.sizeKwdc * 1000)) || 0;
      result.perWdc.total += result.perWdc.nrsFee;
      result.perWdc.showBuyerFees = !isItcProject;
    }
    if (result.total.buyerStorageFee && hasStorage) {
      const perWdcStorageFee = calculateStorageFeePerWdc(project, result.total.buyerStorageFee) ?? 0;
      result.perWdc.buyerStorageFee = perWdcStorageFee;
      result.perWdc.total += perWdcStorageFee;
      result.perWdc.showBuyerStorageFees = !isItcProject && result.perWdc.buyerStorageFee !== 0;
      result.perKwh.buyerStorageFee = result.total.buyerStorageFee / getProjectStorageSizeKwh(project);
    }
  }

  if (isDeveloper) {
    if (result.total.sellerFee) {
      result.perWdc.sellerFee = (result.total.sellerFee / (result.sizeKwdc * 1000)) || 0;
      result.perWdc.total -= result.perWdc.sellerFee;
      result.perWdc.showSellerFees = !isItcProject && result.perWdc.sellerFee !== 0;
    }
    if (result.total.sellerStorageFee && hasStorage) {
      const perWdcStorageFee = calculateStorageFeePerWdc(project, result.total.sellerStorageFee) ?? 0;
      result.perWdc.sellerStorageFee = perWdcStorageFee;
      result.perWdc.total -= perWdcStorageFee;
      result.perWdc.showSellerStorageFees = !isItcProject && result.perWdc.sellerStorageFee !== 0;
      result.perKwh.sellerStorageFee = result.total.sellerStorageFee / getProjectStorageSizeKwh(project);
    }
  }

  // ITC costs
  if (isItcProject) {
    result.itc.investorBidAmount = bid?.amount as number;
    result.itc.percentOfBid = result.expectedItcCost * ((bid?.amount ?? 0) / 100);

    if (isInvestor) {
      result.itc.nrsFee = getFees(project, result.total.epcCost, { bid, hasNrsFee: true }) ?? 0;
      result.itc.buyerStorageFee = 0;

      if (hasStorage) {
        result.itc.buyerStorageFee = getStorageFees(project, { hasBuyerStorageFee: true }) ?? 0;
      }

      result.itc.feesTotal = result.itc.nrsFee + result.itc.buyerStorageFee;
      result.itc.total = result.itc.percentOfBid + result.itc.feesTotal;
    }

    if (isDeveloper) {
      result.itc.sellerFee = getFees(project, result.total.epcCost, { bid, hasSellerFee: true }) ?? 0;
      result.itc.sellerStorageFee = 0;

      if (hasStorage) {
        result.itc.sellerStorageFee = getStorageFees(project, { bid, hasSellerStorageFee: true }) ?? 0;
      }

      result.itc.feesTotal = result.itc.sellerFee + result.itc.sellerStorageFee;
      result.itc.total = result.itc.percentOfBid - result.itc.feesTotal;
    }
  }

  // Offtake costs
  if (isOfftakeProject) {
    result.offtake.investorBidAmount = bid?.amount as number;
    result.offtake.yearOneProduction = System.yearOneProduction(project?.projectArrays);
    result.offtake.revenue = result.offtake.investorBidAmount && (result.offtake.yearOneProduction * result.offtake.investorBidAmount);
  }

  // storage-only costs per kWh
  if (isStorageOnly) {
    result.epcCostTotal = normalizeToOverallStorage(totalStorageKwh, { amount: result.epcCost, unit: result.epcCostUnit } as ProjectBid);
    result.total.bid = bid?.bid ? (bid.bid.unit === 'KWH' ? bid.bid.amount * totalStorageKwh : bid.bid.amount) : 0;
    result.perKwh.bid = bid?.bid ? (bid.bid.unit === 'KWH' ? bid.bid.amount : (bid.bid.amount / totalStorageKwh)) : 0;
    result.total.showBuyerFees = false;
    result.total.showSellerFees = false;
    result.total.epcCost = normalizeToOverallStorage(totalStorageKwh, { amount: result.epcCost, unit: result.epcCostUnit } as ProjectBid);
    result.total.investorBidAmount = result.total.epcCost;
    result.total.interconnection = normalizeToOverallStorage(totalStorageKwh, interconnection);
    result.total.otherItc = normalizeToOverallStorage(totalStorageKwh, otherItc);
    result.total.otherNonItc = normalizeToOverallStorage(totalStorageKwh, otherNonItc);
    result.total.total = result.total.interconnection + result.total.otherItc + result.total.otherNonItc + result.total.epcCost;
    const sizeKwh = System.getProjectStorageSizeKwh(project);

    result.epcCostTotal = normalizeToOverallStorage(totalStorageKwh, { amount: result.epcCost, unit: result.epcCostUnit } as ProjectBid);

    result.perKwh.interconnection = sizeKwh ? (result.total.interconnection / sizeKwh) : 0;
    result.perKwh.otherItc = sizeKwh ? (result.total.otherItc / sizeKwh) : 0;
    result.perKwh.otherNonItc = sizeKwh ? (result.total.otherNonItc / sizeKwh) : 0;
    result.perKwh.epcCost = sizeKwh ? (result.total.epcCost / sizeKwh) : 0;
    result.perKwh.total = result.perKwh.interconnection + result.perKwh.otherItc + result.perKwh.otherNonItc + result.perKwh.epcCost;

    if (isInvestor) {
      if (result.total.nrsFee) {
        result.perKwh.nrsFee = sizeKwh ? (result.total.nrsFee / sizeKwh) : 0;
        result.perKwh.total += result.perKwh.nrsFee;
        result.perKwh.showBuyerFees = !isItcProject;
      }
      if (result.total.buyerStorageFee && hasStorage) {
        const perKwhStorageFee = sizeKwh ? (result.total.buyerStorageFee / sizeKwh) : 0;
        result.perKwh.buyerStorageFee = perKwhStorageFee;
        result.perKwh.total += perKwhStorageFee;
        result.perKwh.showBuyerStorageFees = !isItcProject && result.perKwh.buyerStorageFee !== 0;
        result.total.total += result.total.buyerStorageFee;
      }
    }

    if (isDeveloper) {
      if (result.total.sellerFee) {
        result.perKwh.sellerFee = sizeKwh ? (result.total.sellerFee / sizeKwh) : 0;
        result.perKwh.total -= result.perKwh.sellerFee;
        result.perKwh.showSellerFees = !isItcProject && result.perKwh.sellerFee !== 0;
      }
      if (result.total.sellerStorageFee && hasStorage) {
        const perKwhStorageFee = sizeKwh ? (result.total.sellerStorageFee / sizeKwh) : 0;
        result.perKwh.sellerStorageFee = perKwhStorageFee;
        result.perKwh.total -= perKwhStorageFee;
        result.perKwh.showSellerStorageFees = !isItcProject && result.perKwh.sellerStorageFee !== 0;
        result.total.total -= result.total.sellerStorageFee;
      }
    }
  }

  return result;
};



export default calculateProjectCostsForBids;